import {
    CellTypeNames,
    DateStringFormatOptions,
    FormatOptions,
    TextAlignment,
    TimeStringFormatOptions,
    VerticalAlignment,
} from './CellTypeConstants';
import { RawDraftContentState } from 'draft-js';
import { CellType, CellValue, CellValueDetailedType, DetailedCellError } from 'hyperformula/es';

export enum TableAxis {
    ROW = 'row',
    COL = 'col',
}

export enum TableOperation {
    INSERT = 'insert',
    REMOVE = 'remove',
}

export enum TableChangeSource {
    /* Handsontable built-in table change sources.*/

    AUTOFILL_FILL = 'Autofill.fill',
    COPY_PASTE_PASTE = 'CopyPaste.paste',
    LOAD_DATA = 'loadData',

    /* Milanote custom table change sources.*/

    RESIZE_HANDLE = 'resizeHandle',

    ADD_COLS_RIGHT = 'addColsRight',
    ADD_COLS_LEFT = 'addColsLeft',
    REMOVE_COLS = 'removeCols',
    ADD_ROWS_ABOVE = 'addRowsAbove',
    ADD_ROWS_BELOW = 'addRowsBelow',
    REMOVE_ROWS = 'removeRows',

    // `CellMeta.*` sources are attached when Milanote updates Handsontable programmatically
    // rather than through direct user input. Hooks use them to recognise sync/type-effect updates
    // and avoid treating them like changes made through Handsontable, such as edit, paste, or autofill changes.
    CELL_META_PREFIX = 'CellMeta',
    CELL_META_INIT = 'CellMeta.init',
    CELL_META_UPDATE_CELL_TEXT_ALIGNMENT = 'CellMeta.updateCellTextAlignment',
    CELL_META_UPDATE_CELL_VERTICAL_ALIGNMENT = 'CellMeta.updateCellVerticalAlignment',
    CELL_META_UPDATE_CELL_BACKGROUND = 'CellMeta.updateCellBackground',
    CELL_META_UPDATE_CELL_TYPE = 'CellMeta.updateCellType',
    CELL_META_UPDATE_CELL_TYPE_FORMAT = 'CellMeta.updateCellTypeFormat',
    CELL_META_UPDATE_CELL_TYPE_EFFECT = 'CellMeta.updateCellType.effect',
}

// NOTE: These two interfaces are suffixed with "Obj" to differentiate them from handsontable types:
//  CellCoords: node_modules/handsontable/3rdparty/walkontable/src/cell/coords.d.ts
//  CellRange: node_modules/handsontable/3rdparty/walkontable/src/cell/range.d.ts
export interface CellCoordsObj {
    row: number;
    col: number;
}
export interface CellRangeObj {
    from: CellCoordsObj;
    to: CellCoordsObj;
}

export type CellSelection = [number, number, number, number];

export type CellSelections = Array<[number, number, number, number]>;

// Handsontable computed value. Formula fields will be in it's computed form.
export type HotComputedCellValue = string | number | boolean | null;

export interface CellData {
    value: string | null;
    draftContent?: RawDraftContentState;

    // resultValue is used to store the last result of a formula, to make formula result is searchable
    resultValue?: string | number | null;

    type?: CellTypeObject;
    textStyle?: string[];
    textAlignment?: TextAlignment;
    verticalAlignment?: VerticalAlignment;
    readOnly?: boolean;
    background?: string | null;
}

export type CellFormulaInfo = {
    hfType: CellType;
    hfDetailedType: CellValueDetailedType;
    hfCellValue: CellValue | DetailedCellError;
    isFormula: boolean;
    isFormulaError: boolean;
};

export type CellDataChange = [number, number | string, CellData, CellData];

export type TableContentData = CellData[][];
export type TableColWidthsGU = number[];

export interface MNTableContent {
    data: TableContentData;
    colWidthsGU: TableColWidthsGU;
    version?: TableDataVersion;
}

export type TableElementChanges = {
    tableContent?: MNTableContent;
    width?: number;
};

export type NumberTypeObject = {
    name: CellTypeNames.NUMBER;
    [FormatOptions.DECIMALS]: number | 'auto';
    [FormatOptions.THOUSANDS_SEPARATOR]: boolean;
};

export type CurrencyTypeObject = {
    name: CellTypeNames.CURRENCY;
    [FormatOptions.DECIMALS]: number | 'auto';
    [FormatOptions.THOUSANDS_SEPARATOR]: boolean;
    [FormatOptions.ACCOUNTING]: boolean;
    [FormatOptions.CURRENCY]?: string;
    [FormatOptions.CURRENCY_DISPLAY]?: string;
};

export type PercentageTypeObject = {
    name: CellTypeNames.PERCENTAGE;
    [FormatOptions.DECIMALS]: number | 'auto';
    [FormatOptions.THOUSANDS_SEPARATOR]: boolean;
};

export type TextTypeObject = {
    name: CellTypeNames.TEXT;
};

export type DateTimeTypeObject = {
    name: CellTypeNames.DATE_TIME;
    [FormatOptions.HAS_TIME]: boolean;
    [FormatOptions.HAS_SECONDS]: boolean;
    [FormatOptions.DATE_FORMAT]: DateStringFormatOptions;
    [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions;
    [FormatOptions.TIME_IN_BRACKETS]: boolean;
};

export type CheckboxTypeObject = {
    name: CellTypeNames.CHECKBOX;
};

export type CellTypeObject =
    | NumberTypeObject
    | CurrencyTypeObject
    | PercentageTypeObject
    | TextTypeObject
    | DateTimeTypeObject
    | CheckboxTypeObject;

// This type will never be saved as a cell type, but is used to indicate that the cell type should be auto/removed
export type AutoCellTypeObject = { name: CellTypeNames.AUTO };

export enum TableDataVersion {
    // In V1, cell content with rich text is stored as stringified draft-js format in `value` field
    V1 = 'V1',

    // In V2, cell content stored with plain text in 'value' and rich text structure in 'draftContent' field
    V2 = 'V2',
}
